import React, { useState } from "react"
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ProtectedRoute } from "./components/common/ProtectedRoute";
import TicketsListingPage from "./pages/TicketsListing"
import TicketCreatePage from "./pages/TicketCreate"
import ElementCreatePage from "./pages/ElementCreate"
import TicketViewPage from "./pages/TicketView"
import ElementViewPage from "./pages/ElementView"
import TicketUpdateViewPage from "./pages/TicketUpdateView"
import TicketUpdateCreatePage from "./pages/TicketUpdateCreate"
import ElementsListingPage from "./pages/ElementsListing"
import TicketClosePage from "./pages/TicketClose"
import BlockScreen from "./components/common/BlockScreen"
import PersonsManagementPage from "./pages/PersonsManagement"
import UsersManagementPage from "./pages/UsersManagement"
import UserPasswordResetPage from "./pages/UserPasswordResetPage";
import NotFoundPage from "./pages/NotFoundPage";
import UserPasswordChangePage from "./pages/UserPasswordChangePage";
import TicketEntitiesPage from "./pages/TicketEntitiesSettings";
import ElementEntitiesPage from "./pages/ElementEntitiesSettings";
import SitesListingPage from "./pages/SitesListing"
import SiteViewPage from "./pages/SiteView"
import SiteCreatePage from "./pages/SiteCreate";
import { ProjectProvider } from "./context/ProjectContext";
import { TicketsFilterProvider } from "./context/TicketsFilterContext";
import CompaniesListingPage from "./pages/CompaniesListing";
import CompanyViewPage from "./pages/CompanyView";
import CompanyCreatePage from "./pages/CompanyCreate";
import AreasListingPage from "./pages/AreasListing";
import AreaCreatePage from "./pages/AreaCreate";
import AreaViewPage from "./pages/AreaView";
import OperationReportsPage from "./pages/OperationReports";
import DashboardPage from "./pages/Dashboard";
import { NotificationProvider } from "./context/NotificationContext";
import { NotificationContainer } from "./components/common/NotificationContainer";
import ProjectSettingsPage from "./pages/ProjectSettings";

const HomePage = React.lazy(() => import("./pages/Home"));


export default function App() {
  const [blockScreen, setBlockScreen] = useState(false);
  const setBlockScreenDelayed = (block: boolean) => {
    block ? setBlockScreen(true) : setTimeout(() => setBlockScreen(false), 1000)
  }

  return (
    <div className="App">
      <ProjectProvider>
        <TicketsFilterProvider>
          <NotificationProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/login" element={<HomePage setBlockScreen={setBlockScreenDelayed} />} />

                <Route path="/" element={<Navigate to='/login' replace />} />

                <Route path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <DashboardPage
                        setBlockScreen={setBlockScreenDelayed}
                      />
                    </ProtectedRoute>
                  } />

                <Route path="/tickets/listing"
                  element={
                    <ProtectedRoute>
                      <TicketsListingPage
                        setBlockScreen={setBlockScreenDelayed}
                      />
                    </ProtectedRoute>
                  } />

                <Route path="/ticket/create"
                  element={
                    <ProtectedRoute>
                      <TicketCreatePage
                        setBlockScreen={setBlockScreenDelayed}
                      />
                    </ProtectedRoute>
                  } />

                <Route path="/:project_code/ticket/view/:ticket_number/:tab"
                  element={
                    <ProtectedRoute>
                      <TicketViewPage
                        setBlockScreen={setBlockScreenDelayed} />
                    </ProtectedRoute>
                  } />

                <Route path="/:project_code/ticket/close/:ticket_number"
                  element={
                    <ProtectedRoute>
                      <TicketClosePage
                        setBlockScreen={setBlockScreenDelayed}
                      />
                    </ProtectedRoute>
                  } />

                <Route path="/ticket/update/create/:ticket_number"
                  element={
                    <ProtectedRoute>
                      <TicketUpdateCreatePage
                        setBlockScreen={setBlockScreenDelayed}
                      />
                    </ProtectedRoute>
                  } />

                <Route path="/:project_code/ticket/update/view/:ticket_number/:update_number"
                  element={
                    <ProtectedRoute>
                      <TicketUpdateViewPage
                        setBlockScreen={setBlockScreenDelayed} />
                    </ProtectedRoute>
                  } />

                <Route path="/elements/listing"
                  element={
                    <ProtectedRoute>
                      <ElementsListingPage
                        setBlockScreen={setBlockScreenDelayed} />
                    </ProtectedRoute>
                  } />

                <Route path="/element/create"
                  element={
                    <ProtectedRoute>
                      <ElementCreatePage
                        setBlockScreen={setBlockScreenDelayed}
                      />
                    </ProtectedRoute>
                  } />

                <Route path="/:project_code/element/view/:element_code"
                  element={
                    <ProtectedRoute>
                      <ElementViewPage
                        setBlockScreen={setBlockScreenDelayed} />
                    </ProtectedRoute>
                  } />

                <Route path="/sites/listing"
                  element={
                    <ProtectedRoute>
                      <SitesListingPage
                        setBlockScreen={setBlockScreenDelayed} />
                    </ProtectedRoute>
                  } />

                <Route path="/site/create"
                  element={
                    <ProtectedRoute>
                      <SiteCreatePage
                        setBlockScreen={setBlockScreenDelayed}
                      />
                    </ProtectedRoute>
                  } />

                <Route path="/:project_code/site/view/:site_code"
                  element={
                    <ProtectedRoute>
                      <SiteViewPage
                        setBlockScreen={setBlockScreenDelayed} />
                    </ProtectedRoute>
                  } />

                <Route path="/companies/listing"
                  element={
                    <ProtectedRoute>
                      <CompaniesListingPage
                        setBlockScreen={setBlockScreenDelayed} />
                    </ProtectedRoute>
                  } />

                <Route path="/company/create"
                  element={
                    <ProtectedRoute>
                      <CompanyCreatePage
                        setBlockScreen={setBlockScreenDelayed}
                      />
                    </ProtectedRoute>
                  } />

                <Route path="/:project_code/company/view/:company_code"
                  element={
                    <ProtectedRoute>
                      <CompanyViewPage
                        setBlockScreen={setBlockScreenDelayed} />
                    </ProtectedRoute>
                  } />

                <Route path="/areas/listing"
                  element={
                    <ProtectedRoute>
                      <AreasListingPage
                        setBlockScreen={setBlockScreenDelayed} />
                    </ProtectedRoute>
                  } />

                <Route path="/area/create"
                  element={
                    <ProtectedRoute>
                      <AreaCreatePage
                        setBlockScreen={setBlockScreenDelayed}
                      />
                    </ProtectedRoute>
                  } />

                <Route path="/:project_code/area/view/:area_code"
                  element={
                    <ProtectedRoute>
                      <AreaViewPage
                        setBlockScreen={setBlockScreenDelayed}
                      />
                    </ProtectedRoute>
                  } />

                <Route path="/operations-reports"
                  element={
                    <ProtectedRoute>
                      <OperationReportsPage
                        setBlockScreen={setBlockScreenDelayed} />
                    </ProtectedRoute>
                  } />


                <Route path="/settings/ticket-entities"
                  element={
                    <ProtectedRoute>
                      <TicketEntitiesPage
                        setBlockScreen={setBlockScreenDelayed} />
                    </ProtectedRoute>
                  } />

                <Route path="/settings/element-entities"
                  element={
                    <ProtectedRoute>
                      <ElementEntitiesPage
                        setBlockScreen={setBlockScreenDelayed} />
                    </ProtectedRoute>
                  } />

                <Route path="/settings/project-settings"
                  element={
                    <ProtectedRoute>
                      <ProjectSettingsPage
                        setBlockScreen={setBlockScreenDelayed} />
                    </ProtectedRoute>
                  } />

                <Route path="/users-management/persons"
                  element={
                    <ProtectedRoute>
                      <PersonsManagementPage
                        setBlockScreen={setBlockScreenDelayed} />
                    </ProtectedRoute>
                  } />

                <Route path="/users-management/change-pwd"
                  element={
                    <ProtectedRoute>
                      <UserPasswordChangePage
                        setBlockScreen={setBlockScreenDelayed} />
                    </ProtectedRoute>
                  } />

                <Route path="/users-management/users"
                  element={
                    <ProtectedRoute>
                      <UsersManagementPage
                        setBlockScreen={setBlockScreenDelayed} />
                    </ProtectedRoute>
                  } />

                <Route path="/users-management/reset-pwd/token-step/:token"
                  element={
                    <UserPasswordResetPage
                      setBlockScreen={setBlockScreenDelayed} />
                  } />

                <Route path="/not-found"
                  element={
                    <NotFoundPage />
                  } />

                <Route path="*" element={<Navigate to='/not-found' replace />} />

              </Routes>
              <NotificationContainer />
            </BrowserRouter>
          </NotificationProvider>
        </TicketsFilterProvider>
      </ProjectProvider>
      {
        blockScreen && (
          <BlockScreen openBlockScreen={blockScreen} />
        )
      }
    </div>
  )
}
